import { FeatureFlag, ProcedureStatus, reschedulableProcedureStatuses } from '@caresend/types';
import { SECOND, arrayToObj, nullishFilter, deduplicateArray, objectFilter, getSupplyInstructionConfigOptionsPath, hasStatus, getFedExTrackingNumberFromURL } from '@caresend/utils';
import '../../../database/algoliaConfig/indices.js';
import { needsNewKey, searchDenormalizedOrders } from '../../../database/algoliaConfig/methods.js';
import { unwrapMaybeEncryptedData } from '../../../database/encryption/decrypt.js';
import 'js-sha256';
import { getAlgoliaOrdersAPIKeyRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import { getStore } from '../../helpers/initStore.js';
import '../../../database/encryption/model.js';
import 'firebase/auth';
import { isProd } from '../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import { flagOn } from '../../modules/variables/index.js';

/**
 * The time in milliseconds to wait before allowing display of data from
 * Algolia for recently scheduled/rescheduled/canceled denormalized draft
 * waypoints.
 */
const USE_LOCAL_DRAFT_WAYPOINT_TIME_THRESHOLD = isProd
    ? SECOND * 18
    : SECOND * 30;
/**
 * This enum is helpful for determining the reason a reschedule is happening,
 * especially when debugging.
 *
 * The `SUPPLY_SIDE_PROCEDURE_CANCELED` is used when we are rescheduling at
 * least one canceled or failed procedure by the supply side
 * (`disallowRescheduling` is `false`).
 *
 * Link to Whimsical:
 * https://whimsical.com/procedure-cancels-fails-reschedulation-6hiZWfUGaHUeif4gXEP1MM
 * (viewable as a workspace member)
 */
var ReschedulingAllowedReason;
(function (ReschedulingAllowedReason) {
    ReschedulingAllowedReason["SUPPLY_SIDE_PROCEDURE_CANCELED"] = "supplySideProcedureCanceled";
    ReschedulingAllowedReason["PROCEDURE_STATUS"] = "procedureStatus";
})(ReschedulingAllowedReason || (ReschedulingAllowedReason = {}));
/**
 * Used to determine which tracking URLs to display
 */
var ShipmentTrackingType;
(function (ShipmentTrackingType) {
    ShipmentTrackingType["PATIENT"] = "patient";
    ShipmentTrackingType["SAMPLE"] = "sample";
})(ShipmentTrackingType || (ShipmentTrackingType = {}));
const isCancelVisitDryRun = (draftWaypoint) => {
    var _a;
    const isDryRunDueToFeatureFlag = flagOn(FeatureFlag.CANCEL_VISIT_DRY_RUN);
    const isDryRunDueToDebugProperty = !!((_a = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.debug) === null || _a === void 0 ? void 0 : _a.dryRun);
    return isDryRunDueToFeatureFlag || isDryRunDueToDebugProperty;
};
/**
 * Check if the algolia key needs to be updated and update it if it does.
 *
 * This function is currently a copy of the one in the pinia orders store. We
 * might want to create a denormalized draft waypoint index in the future to
 * fetch from there. This will allow us to fetch the draft waypoint data without
 * having to fetch the denormalized orders data.
 */
const getUpdatedAlgoliaOrdersAPIKey = async (algoliaKey, isPatientRequest, officeID) => {
    let key = algoliaKey;
    if (!key || needsNewKey(key)) {
        key = await getAlgoliaOrdersAPIKeyRequest({
            isPatientRequest,
            officeID: isPatientRequest ? undefined : officeID,
        });
    }
    return key;
};
const getDraftWaypointsFromDenormalizedOrders = (denormalizedOrders) => {
    const denormalizedDraftWaypoints = {};
    Object.entries(denormalizedOrders).forEach(([_, denormalizedOrder]) => {
        const { draftWaypoints } = denormalizedOrder;
        if (!draftWaypoints)
            return;
        Object.entries(draftWaypoints).forEach(([id, draftWaypoint]) => {
            denormalizedDraftWaypoints[id] = draftWaypoint;
        });
    });
    return denormalizedDraftWaypoints;
};
const getOrdersForDraftWaypointsFromAlgolia = async (denormalizedOrderKey, draftWaypointID) => {
    const facetFilters = draftWaypointID
        ? [`draftWaypointIDs:${draftWaypointID}`]
        : [];
    const algoliaOrders = await searchDenormalizedOrders(denormalizedOrderKey, '', { facetFilters });
    return arrayToObj(algoliaOrders.hits, 'id');
};
const getPatientIDsOnProcedureInfos = (procedureInfos) => {
    const patientIDs = Object.values(procedureInfos).map((procedureInfo) => { var _a; return (_a = procedureInfo.patientID) !== null && _a !== void 0 ? _a : null; }).filter(nullishFilter);
    return deduplicateArray(patientIDs);
};
const isCurrentUserOnDraftWaypoint = (draftWaypoint) => {
    var _a, _b, _c;
    const store = getStore();
    const userID = (_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.id;
    if (!userID)
        return false;
    const procedureInfos = (_c = (_b = draftWaypoint.patientProcedureData) === null || _b === void 0 ? void 0 : _b.procedures) !== null && _c !== void 0 ? _c : {};
    const patientsOnDraftWaypoint = getPatientIDsOnProcedureInfos(procedureInfos);
    if (patientsOnDraftWaypoint.includes(userID))
        return true;
    if (draftWaypoint.schedulingUserID === userID)
        return true;
    return false;
};
const getDraftWaypointsFilteredByCurrentUser = (draftWaypoints) => objectFilter(draftWaypoints, (draftWaypoint) => isCurrentUserOnDraftWaypoint(draftWaypoint));
const decryptDenormalizedDraftWaypoints = async (denormalizedDraftWaypoints) => {
    const decryptedDraftWaypointPromises = Object.values(denormalizedDraftWaypoints).map(async (denormalizedDraftWaypoint) => {
        const [location, schedulingUserID, schedulingUserName, patientProcedureData, primaryContactName,] = await Promise.all([
            unwrapMaybeEncryptedData(denormalizedDraftWaypoint.location, `ddw-${denormalizedDraftWaypoint.id}-location`),
            unwrapMaybeEncryptedData(denormalizedDraftWaypoint.schedulingUserID, `ddw-${denormalizedDraftWaypoint.id}-schedulingUserID`),
            unwrapMaybeEncryptedData(denormalizedDraftWaypoint.schedulingUserName, `ddw-${denormalizedDraftWaypoint.id}-schedulingUserName`),
            unwrapMaybeEncryptedData(denormalizedDraftWaypoint.patientProcedureData, `ddw-${denormalizedDraftWaypoint.id}-patientProcedureData`),
            unwrapMaybeEncryptedData(denormalizedDraftWaypoint.primaryContactName, `ddw-${denormalizedDraftWaypoint.id}-primaryContactName`),
        ]);
        return {
            ...denormalizedDraftWaypoint,
            location,
            schedulingUserID,
            schedulingUserName,
            patientProcedureData,
            primaryContactName,
        };
    });
    const decryptedDraftWaypoints = await Promise.all(decryptedDraftWaypointPromises);
    return arrayToObj(decryptedDraftWaypoints, 'id');
};
const getRecentLocallyStoredDraftWaypointIDs = (locallyStoredDraftWaypointIDs) => objectFilter(locallyStoredDraftWaypointIDs, (timestamp) => Date.now() - timestamp <= USE_LOCAL_DRAFT_WAYPOINT_TIME_THRESHOLD);
const fetchSupplyInstructionsForDraftWaypoint = async (draftWaypointID, getters, isRescheduling) => {
    const supplyInstructionsByProcedureID = {};
    const procedureIDs = getters.getProcedureIDsOnDraftWaypoint(draftWaypointID, isRescheduling);
    const promises = procedureIDs.map(async (procedureID) => {
        var _a;
        let supplyInstructionConfigOptions = getters.getSupplyInstructionsOnProcedure(procedureID);
        if (supplyInstructionConfigOptions) {
            supplyInstructionsByProcedureID[procedureID] = supplyInstructionConfigOptions;
            return;
        }
        supplyInstructionConfigOptions = (_a = await getValueOnce(getSupplyInstructionConfigOptionsPath(procedureID))) !== null && _a !== void 0 ? _a : undefined;
        if (!supplyInstructionConfigOptions)
            return;
        supplyInstructionsByProcedureID[procedureID] = supplyInstructionConfigOptions;
    });
    await Promise.all(promises);
    return supplyInstructionsByProcedureID;
};
/**
 * Passes if at least one procedure on the draft waypoint does not have a
 * status of canceled, completed, or failed. Rescheduling uses existing
 * procedures, so it is expected that they would not have these statuses.
 *
 * Additionally, supply instruction config options are required for scheduling,
 * and unless `disallowRes`cheduling` is false, they are deleted when a procedure
 * reaches one of these statuses, so rescheduling is no longer possible.
 */
const getReschedulingAllowedBasedOnProcedureStatus = (draftWaypointID, getters) => {
    const procedureInfos = getters.getProcedureInfoOnDraftWaypoint(draftWaypointID);
    const ineligibleProcedureStatuses = [
        ProcedureStatus.CANCELED,
        ProcedureStatus.COMPLETED,
        ProcedureStatus.FAILED,
    ];
    const someProcedureIneligible = procedureInfos.some((procedureInfo) => hasStatus(procedureInfo, ineligibleProcedureStatuses));
    return !someProcedureIneligible;
};
/**
 * We allow rescheduling in the event that at least one procedure is canceled
 * or failed and `disallowRescheduling` is specifically `false`.
 *
 * In the event that we have a reschedule due to this, we will filter out the
 * procedures that are not eligible for rescheduling.
 */
const getReschedulingAllowedDueToSupplySideProcedureCancelation = (draftWaypointID, getters) => {
    const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
    if (!patientProcedureData)
        return false;
    const someProcedureIsReschedulable = Object.values(patientProcedureData.procedures).some((procedureInfo) => hasStatus(procedureInfo.status, reschedulableProcedureStatuses)
        && procedureInfo.disallowRescheduling === false);
    return someProcedureIsReschedulable;
};
/**
 * Returns a list of patients and the tests they are scheduled to perform.
 *
 * If a procedure has multiple tests, we will only include the task's name instead
 */
const getPatientAndTestNames = (patientProcedureData, tasks) => {
    const procedures = Object.values(patientProcedureData.procedures);
    const hasMultipleTests = procedures.some(({ testNames }) => testNames && testNames.length > 1);
    const patientMap = procedures.reduce((map, { patientName, testNames, bookingID, taskID }) => {
        const patientData = map.get(patientName) || {
            testNames: new Set(),
            bookingID,
        };
        const task = tasks === null || tasks === void 0 ? void 0 : tasks[taskID];
        if (hasMultipleTests && task) {
            patientData.testNames.add(task.info.friendlyName);
        }
        else {
            testNames === null || testNames === void 0 ? void 0 : testNames.forEach((testName) => patientData.testNames.add(testName));
        }
        map.set(patientName, patientData);
        return map;
    }, new Map());
    return Array.from(patientMap.entries()).map(([patientName, data]) => ({
        patientName,
        testNames: Array.from(data.testNames),
        bookingID: data.bookingID,
    }));
};
/**
 * Returns a list of patients and each of their tests, grouped by the tracking
 * URL.
 */
const getPatientsAndTestsByTrackingURL = (patientProcedureData, type) => {
    var _a;
    const store = getStore();
    const patientsAndTestsByTrackingURL = {};
    const patientAndTestNames = getPatientAndTestNames(patientProcedureData, (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.tasks);
    patientAndTestNames.forEach((patientAndTestName) => {
        var _a, _b;
        const { bookingID } = patientAndTestName;
        const { shipmentTracking } = patientProcedureData;
        if (!shipmentTracking)
            return;
        const trackingURL = type === ShipmentTrackingType.PATIENT
            ? (_a = shipmentTracking.patientTrackingURLs) === null || _a === void 0 ? void 0 : _a[bookingID]
            : (_b = shipmentTracking.sampleTrackingURLs) === null || _b === void 0 ? void 0 : _b[bookingID];
        if (!trackingURL)
            return;
        patientsAndTestsByTrackingURL[trackingURL] = [
            ...(patientsAndTestsByTrackingURL[trackingURL] || []),
            patientAndTestName,
        ];
    });
    return patientsAndTestsByTrackingURL;
};
/**
 * Returns all sample shipment information including tracking numbers, patient
 * names, and test names. Returns data for all bookings regardless of whether
 * tracking numbers exist, allowing users to update missing tracking numbers.
 * Maintains 1:1 relationship between bookings and shipment info.
 */
const getAllSampleShipmentInfo = (patientProcedureData) => {
    var _a;
    const shipmentInfoList = [];
    const { shipmentTracking } = patientProcedureData;
    const store = getStore();
    const tasks = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.tasks;
    const procedures = Object.values(patientProcedureData.procedures);
    const bookingMap = new Map();
    procedures.forEach((procedure) => {
        var _a;
        const { bookingID, patientName } = procedure;
        const trackingURL = (_a = shipmentTracking === null || shipmentTracking === void 0 ? void 0 : shipmentTracking.sampleTrackingURLs) === null || _a === void 0 ? void 0 : _a[bookingID];
        const trackingNumber = trackingURL ? (getFedExTrackingNumberFromURL(trackingURL) || '') : '';
        if (!bookingMap.has(bookingID)) {
            bookingMap.set(bookingID, {
                procedures: [],
                patientName,
                trackingNumber,
            });
        }
        const bookingData = bookingMap.get(bookingID);
        if (bookingData) {
            bookingData.procedures.push(procedure);
        }
    });
    bookingMap.forEach((bookingData, bookingID) => {
        var _a;
        const { procedures: bookingProcedures, patientName, trackingNumber } = bookingData;
        const hasMultipleTests = bookingProcedures.some(({ testNames }) => testNames && testNames.length > 1);
        let testOrTaskName;
        if (hasMultipleTests) {
            const firstProcedure = bookingProcedures[0];
            if (firstProcedure) {
                const task = tasks === null || tasks === void 0 ? void 0 : tasks[firstProcedure.taskID];
                testOrTaskName = (_a = task === null || task === void 0 ? void 0 : task.info) === null || _a === void 0 ? void 0 : _a.friendlyName;
            }
        }
        else {
            const allTestNames = bookingProcedures
                .flatMap((procedure) => procedure.testNames || [])
                .filter((testName, index, array) => array.indexOf(testName) === index);
            testOrTaskName = allTestNames.join(', ');
        }
        shipmentInfoList.push({
            bookingID,
            trackingNumber,
            patientName,
            testOrTaskName,
        });
    });
    return shipmentInfoList;
};

export { ReschedulingAllowedReason, ShipmentTrackingType, USE_LOCAL_DRAFT_WAYPOINT_TIME_THRESHOLD, decryptDenormalizedDraftWaypoints, fetchSupplyInstructionsForDraftWaypoint, getAllSampleShipmentInfo, getDraftWaypointsFilteredByCurrentUser, getDraftWaypointsFromDenormalizedOrders, getOrdersForDraftWaypointsFromAlgolia, getPatientAndTestNames, getPatientIDsOnProcedureInfos, getPatientsAndTestsByTrackingURL, getRecentLocallyStoredDraftWaypointIDs, getReschedulingAllowedBasedOnProcedureStatus, getReschedulingAllowedDueToSupplySideProcedureCancelation, getUpdatedAlgoliaOrdersAPIKey, isCancelVisitDryRun };
